/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { MutableRefObject } from 'react';
import { useMemo, useState } from 'react';

import { TabScrollAffordance, useTabScrollAffordance } from '@coursera-web/cds-labs';

import {
  Divider,
  Grid,
  Hidden,
  SelectField,
  SelectOption,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  Typography2 as Typography,
  breakpoints,
} from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import CareerRolePanel from 'bundles/premium-hub/components/career-module/CareerRolePanel';
import Container from 'bundles/premium-hub/components/shared/Container';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { CareerRolesModuleData } from 'bundles/premium-hub/types';

import _t from 'i18n!nls/premium-hub';

type Props = {
  moduleData?: CareerRolesModuleData | null;
};

const styles = {
  moduleContainer: css`
    margin-top: var(--cds-spacing-400);
  `,
  tabsWrapper: css`
    position: relative;
    overflow: hidden;
  `,
  tabList: css`
    & button {
      padding: 0 var(--cds-spacing-100);
      margin-right: var(--cds-spacing-300);
    }

    & .cds-tab-selected {
      color: var(--cds-color-blue-750) !important;
    }

    & .cds-tab-list-indicator {
      background-color: var(--cds-color-blue-750);
    }
  `,
  tabPanel: css`
    padding-top: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      padding-top: var(--cds-spacing-200);
    }
  `,
  selectFieldContainer: css`
    width: 100%;
    margin-top: var(--cds-spacing-200);
  `,
  fullWidth: css`
    width: 100%;
  `,
  divider: css`
    margin: calc(var(--cds-spacing-400) + var(--cds-spacing-100)) 0;
  `,
};

const CareerRolesModule = ({ moduleData }: Props) => {
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const visibilityTrackingRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_section', {
    pageSection: { sectionName: 'career_info', index: sectionIndex },
  });
  const track = useTracker();
  const { tabsWrapperRef, canScrollBackward, canScrollForward, scrollBackward, scrollForward } =
    useTabScrollAffordance();

  const [currentRoleId, setCurrentRoleId] = useState(moduleData?.roleItems?.[0]?.roleId);
  const currentRole = useMemo(() => {
    return moduleData?.roleItems?.find((item) => item?.roleId === currentRoleId);
  }, [currentRoleId, moduleData]);

  if (!moduleData || !moduleData.roleItems || !moduleData.roleItems.length) return null;

  const handleTabChange = (newRoleId: string) => {
    setCurrentRoleId(newRoleId);
    track('click_tab', {
      tab: {
        name: 'dcat_career_module_role_tabs',
        selectedTab: newRoleId,
      },
    });
  };

  return (
    <Container css={styles.moduleContainer}>
      <div ref={visibilityTrackingRef}>
        <Grid container spacing={12}>
          <Grid item xs={12}>
            <Typography variant="subtitleLarge" component="h2">
              {moduleData.title}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TabContext value={currentRoleId ?? ''}>
              <Hidden xsDown>
                <div ref={tabsWrapperRef} css={styles.tabsWrapper}>
                  <Tabs
                    variant="section"
                    aria-label={_t('Career Roles Tabs')}
                    onChange={handleTabChange}
                    css={styles.tabList}
                  >
                    {moduleData.roleItems.map((item) => (
                      <Tab key={item.roleId} label={item.roleName ?? ''} value={item?.roleId ?? ''} />
                    ))}
                  </Tabs>
                  <TabScrollAffordance
                    canScrollBackward={canScrollBackward}
                    canScrollForward={canScrollForward}
                    onScrollBackward={scrollBackward}
                    onScrollForward={scrollForward}
                  />
                </div>
              </Hidden>
              <Hidden smUp css={styles.selectFieldContainer}>
                <SelectField
                  aria-label={_t('Career roles')}
                  value={currentRoleId ?? ''}
                  onChange={(e) => handleTabChange(e.target.value as string)}
                  css={styles.fullWidth}
                >
                  {moduleData.roleItems.map((item) => (
                    <SelectOption key={item.roleId} value={item.roleId ?? ''}>
                      {item.roleName}
                    </SelectOption>
                  ))}
                </SelectField>
              </Hidden>
              <TabPanel value={currentRoleId ?? ''} css={styles.tabPanel}>
                <CareerRolePanel role={currentRole} />
              </TabPanel>
            </TabContext>
          </Grid>
        </Grid>
        <Divider color="light" orientation="horizontal" css={styles.divider} />
      </div>
    </Container>
  );
};

export default CareerRolesModule;
